/* eslint-disable unicorn/prevent-abbreviations */
import { Components, createTheme, PaletteOptions, SimplePaletteColorOptions } from '@mui/material/styles';
import React from 'react';

// https://mui.com/customization/typography/#adding-amp-disabling-variants
// https://mui.com/customization/palette/#adding-new-colors
declare module '@mui/material/styles' {
  interface Palette {
    inverted: Palette['primary'];
    highlight: { main: string; contrastText: string };
  }
  interface PaletteOptions {
    inverted: PaletteOptions['primary'];
    highlight?: { main: string; contrastText: string };
  }
  interface TypeBackground {
    surface: string;
    backdrop: string;
  }
  interface TypographyVariants {
    label: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
  }
  interface PaletteColor {
    hover?: string;
    lightest?: string;
    lighter?: string;
    darker?: string;
    darkest?: string;
    focusRing?: string;
  }
  interface SimplePaletteColorOptions {
    hover?: string;
    lightest?: string;
    lighter?: string;
    darker?: string;
    darkest?: string;
    focusRing?: string;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    inverted: true;
  }
  interface ButtonPropsVariantOverrides {
    info: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    inverted: true;
  }
  interface ChipPropsVariantOverrides {
    info: true;
  }
}

declare module '@mui/material/Slider' {
  interface SliderPropsColorOverrides {
    info: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    label: true;
  }
}

// Available font-weight values 300;400;500;600;700;900
// add more through Google Fonts/_document.tsx
export const themeTypography = {
  fontFamily: 'Work Sans, sans-serif',
  h1: {
    fontSize: '4rem',
    fontWeight: 900,
    lineHeight: 1.5,
  },
  h2: {
    fontSize: '3.5rem',
    fontWeight: 700,
    lineHeight: 1.6,
  },
  h3: {
    fontSize: '3rem',
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h4: {
    fontSize: '2.5rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: '1.75rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '1.25rem',
    fontWeight: 300,
    lineHeight: 1.5,
  },
  subtitle1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.75,
  },
  subtitle2: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.5,
  },
  body1: {
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.4,
  },
  button: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.75,
  },
  overline: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 2,
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  label: {
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.1,
  },
};

const lightPalette: PaletteOptions = {
  mode: 'light',
  common: {
    white: '#ffffff',
    black: '#000000',
  },
  primary: {
    main: '#23252f',
    light: '#4f5058',
    dark: '#040509',
  },
  secondary: {
    hover: '#d5e2d4',
    lightest: '#f4fdf2',
    lighter: '#92C5AA',
    light: '#5f9277',
    main: '#387755',
    dark: '#27533b',
    focusRing: 'rgba(58, 133, 137, 0.16)',
  },
  info: {
    hover: '#d5dae3',
    lightest: '#e8f2fd',
    lighter: '#537ec1',
    light: '#3773e0',
    main: '#295eb2',
    dark: '#1c417c',
  },
  inverted: {
    main: '#ffffff',
    light: '#fbfbfb',
    dark: '#eaeaea',
  },
  warning: {
    main: '#ffb700',
    light: '#ffc533',
    dark: '#b28000',
  },
  success: {
    main: '#4ca475',
    light: '#6fb690',
    dark: '#357251',
  },
  error: {
    main: '#ee4b4b',
    light: '#f16f6f',
    dark: '#a63434',
  },
  background: {
    default: '#f5f5f5',
    paper: '#ffffff',
    surface: '#f0f0f0',
    backdrop: '#000000',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
  action: {
    hover: 'rgba(0, 0, 0, 0.04)',
  },
  highlight: {
    main: '#ffeb3b',
    contrastText: '#000000',
  },
};

const darkPalette: PaletteOptions = {
  mode: 'dark',
  common: {
    white: '#ffffff',
    black: '#000000',
  },
  primary: {
    main: '#e0e0e0',
    light: '#f5f5f5',
    dark: '#bdbdbd',
  },
  secondary: {
    hover: '#2a3d2e',
    lightest: '#1a2e1f',
    lighter: '#6fa888',
    light: '#7fbf99',
    main: '#4a9e6e',
    dark: '#3a7a54',
    focusRing: 'rgba(74, 158, 110, 0.16)',
  },
  info: {
    hover: '#1e2a3d',
    lightest: '#1a2538',
    lighter: '#6b93d4',
    light: '#5b8be8',
    main: '#4a7fd4',
    dark: '#3a6fc4',
  },
  inverted: {
    main: '#1e1e1e',
    light: '#2a2a2a',
    dark: '#141414',
  },
  warning: {
    main: '#ffb700',
    light: '#ffc533',
    dark: '#b28000',
  },
  success: {
    main: '#5cb882',
    light: '#7fc99a',
    dark: '#408060',
  },
  error: {
    main: '#f06060',
    light: '#f58080',
    dark: '#c04040',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
    surface: '#2a2a2a',
    backdrop: '#121212',
  },
  text: {
    primary: '#e0e0e0',
    secondary: '#a0a0a0',
  },
  divider: 'rgba(255, 255, 255, 0.12)',
  action: {
    hover: 'rgba(255, 255, 255, 0.08)',
  },
  highlight: {
    main: '#b39200',
    contrastText: '#ffffff',
  },
};

const getComponents = (mode: 'light' | 'dark'): Components => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  const info = palette.info as SimplePaletteColorOptions;
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
      variants: [
        {
          props: { variant: 'info' },
          style: {
            textTransform: 'none',
            background: info.lightest,
            color: info.main,
            fontWeight: 500,
            '&:hover': {
              background: info.hover,
            },
          },
        },
      ],
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'info' },
          style: {
            textTransform: 'none',
            background: info.lightest,
            color: info.main,
            fontWeight: 500,
            borderRadius: '4px',
            '&:hover': {
              background: info.hover,
            },
            '.MuiChip-label': {
              flexGrow: 1,
            },
          },
        },
      ],
    },
    MuiOutlinedInput: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          borderRadius: '2px',
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        followCursor: true,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#ffffff',
          color: mode === 'dark' ? '#e0e0e0' : '#000000',
          boxShadow: '0 2px 2px rgba(0, 0, 0, 0.05)',
          border: mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.1)',
          transition:
            'opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        sortButton: {
          padding: 2,
        },
      },
    },
  };
};

export const generateTheme = (mode: 'light' | 'dark') => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;
  return createTheme({
    typography: themeTypography,
    palette,
    spacing: 8,
    shape: {
      borderRadius: 4,
    },
    components: getComponents(mode),
  });
};

export type Theme = ReturnType<typeof generateTheme>;

export const HEADER_HEIGHT = 64;

export const SIDEBAR_WIDTH = 340;
